/* eslint-disable no-nested-ternary */
import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import { isMobileDevice } from "../../utils/generalUtils";
import HallwayIcon1 from "./HallwayIcon1.png";
import VolumeMute from "./VolumeMute.png";
import VolumeMax from "./VolumeMax.png";
import VolumeMid from "./VolumeMid.png";
import { HallwaySlider } from "../../components/common/slider/HallwaySlider";
import { fetchUserName, fetchUserId } from "../../redux/slices/users";
import HallwayStyles from "./HallwayStyles";
import { socket } from "../../socket";
import { getFromSessionStorage, setToSessionStorage } from "../../hooks/useSessionStorage";

const Hallway = ({ netcoins, location, onFocusChange, setIsHallwayOpen, isHallwayOpen, hallwayPrompt }) => {
    const wrapperRef = useRef(null);
    const isMobile = isMobileDevice();
    const [hallwayScriptLoaded, setHallwayScriptLoaded] = useState(false);
    const userId = useSelector(fetchUserId);
    const username = useSelector(fetchUserName);
    // Set characterId based on netcoins prop
    const [characterId, setCharacterId] = useState(
        netcoins ? "character_de7a2311b2fd4b18" : "f73be318-7427-48e9-88ec-b5272375290f"
    );
    // Volume state
    const [onVolumeOpen, setOnVolumeOpen] = useState(false);
    const [volume, setVolume] = useState(100);
    // Volume handlers
    const handleVolumeOpen = () => {
        setOnVolumeOpen((prev) => !prev);
    };

    const handleVolumeChange = (newVolume) => {
        setVolume(newVolume);
        // Send volume to embed if possible
        const baseEl = wrapperRef.current?.querySelector("hallway-embed");
        if (baseEl && typeof baseEl.setVolume === "function") {
            baseEl.setVolume(newVolume < 10 ? 0 : newVolume / 100);
        } else if (baseEl?.shadowRoot) {
            // fallback: try to postMessage if available
            const expandedEl = baseEl.shadowRoot.querySelector("hallway-embed-expanded");
            if (expandedEl && typeof expandedEl.setVolume === "function") {
                expandedEl.setVolume(newVolume < 10 ? 0 : newVolume / 100);
            }
        }
    };

    useEffect(() => {
        handleVolumeChange(volume);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volume]);

    const handleRefocus = () => {
        if (onFocusChange) {
            onFocusChange();
        }
    };

    useEffect(() => {
        // Update characterId when netcoins changes
        setCharacterId(netcoins ? "character_de7a2311b2fd4b18" : "f73be318-7427-48e9-88ec-b5272375290f");

        // Remove previous script if present
        const prevScript = document.querySelector('script[src="https://hallway.ai/embed-loader.js"]');
        if (prevScript) {
            document.body.removeChild(prevScript);
        }

        if (!isHallwayOpen) return;

        const script = document.createElement("script");
        script.src = "https://hallway.ai/embed-loader.js";
        script.async = true;

        script.onload = () => {
            setHallwayScriptLoaded(true);
            console.log("Hallway script loaded");
        };

        script.onerror = () => {
            console.error("Failed to load hallway script");
        };

        document.body.appendChild(script);

        return () => {
            if (script.parentNode) {
                document.body.removeChild(script);
            }
            setHallwayScriptLoaded(false);
        };
    }, [isHallwayOpen, netcoins]);

    // Listen for 'connected' event from hallway-embed and send hello only once
    useEffect(() => {
        if (!isHallwayOpen || !hallwayScriptLoaded) return;

        const baseEl = wrapperRef.current?.querySelector("hallway-embed");
        if (!baseEl) return;

        // Limit initial greetings to 2 times per user (persisted across sessions) to avoid
        // repetitive introductions while maintaining some familiarity for returning users
        const onConnected = () => {
            const helloCount = parseInt(getFromSessionStorage("botHelloCount") || "0", 10);
            if (helloCount < 5) {
                handleSend(`Hello! My name is ${username}`);
                setToSessionStorage("botHelloCount", (helloCount + 1).toString());
            }
        };
        baseEl.addEventListener("connected", onConnected);

        return () => {
            baseEl.removeEventListener("connected", onConnected);
        };
    }, [isHallwayOpen, hallwayScriptLoaded, username]);

    useEffect(() => {
        socket.on("connect", () => {
            console.log("Connected to server", socket.id);
        });
        socket.on("toHallway", async (response) => {
            console.log("Received from socket, send toHallway:", response);
            if (userId === response.userId && !netcoins) {
                handleSend(response.prompt);
            }
        });

        socket.on("fromHallway", async (response) => {
            console.log("Received fromHallway:", response);
        });

        // Cleanup listeners on unmount
        return () => {
            socket.off("connect");
            socket.off("toHallway");
            socket.off("fromHallway");
        };
    }, [userId]);

    const CustomTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(
        () => ({
            [`& .${tooltipClasses.tooltip}`]: {
                backgroundColor: "#000",
                color: "#fff",
                border: "1px solid rgba(255, 255, 255, 0.20)",
                padding: "15px",
                boxShadow: "0px 25px 35px 0px rgba(255, 255, 255, 0.05)",
                fontSize: 11,
            },
        })
    );

    const handleToggle = () => {
        if (isHallwayOpen) {
            setOnVolumeOpen(false);
        }
        setIsHallwayOpen(!isHallwayOpen);
        window.Vagon?.focusIframe();
    };

    const handleSend = (text) => {
        const baseEl = wrapperRef.current?.querySelector("hallway-embed");
        const expandedEl = baseEl?.shadowRoot?.querySelector("hallway-embed-expanded");

        if (expandedEl && typeof expandedEl.sendUserContent === "function") {
            expandedEl.sendUserContent(text);
        } else {
            console.warn("sendUserContent not available — iframe not ready yet.");
        }
    };

    useEffect(() => {
        if (hallwayPrompt) {
            handleSend(hallwayPrompt);
        }
    }, [hallwayPrompt, username]);

    return (
        <>
            <HallwayStyles />
            <CustomTooltip placement="right" title="Hide or show your Intraverse companion.">
                <ToggleHallwayIcon
                    src={HallwayIcon1}
                    alt="Toggle Chat"
                    isHallwayOpen={isHallwayOpen}
                    onClick={handleToggle}
                />
            </CustomTooltip>
            <IframeContainer isHallwayOpen={isHallwayOpen}>
                {isHallwayOpen && hallwayScriptLoaded && (
                    <>
                        <WidgetWrapper className="hallway-instance" isHallwayOpen={isHallwayOpen} ref={wrapperRef}>
                            <VolumeContainer>
                                <VolumeSquare volume={volume} volumeOpen={onVolumeOpen} onClick={handleVolumeOpen}>
                                    <img
                                        src={
                                            volume < 10
                                                ? VolumeMute
                                                : volume < 50
                                                  ? VolumeMid
                                                  : volume > 50
                                                    ? VolumeMax
                                                    : VolumeMid
                                        }
                                        alt="volume icon"
                                    />
                                </VolumeSquare>
                                {onVolumeOpen && (
                                    <SliderWrapper>
                                        <HallwaySlider
                                            onChange={handleVolumeChange}
                                            highValue={100}
                                            lowValue={0}
                                            value={volume}
                                            vertical
                                        />
                                    </SliderWrapper>
                                )}
                            </VolumeContainer>
                            <hallway-embed
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    border: "none",
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                }}
                                volume={volume}
                                data-location="right"
                                character-id={characterId}
                                theme="dark"
                                query="hideControls=true"
                                disable-mobile-styling="true"
                            />
                        </WidgetWrapper>
                    </>
                )}
                {location === "inGame" && !isMobile && (
                    <RefocusButton isHallwayOpen={isHallwayOpen} onClick={handleRefocus} type="button">
                        Resume movement
                    </RefocusButton>
                )}
            </IframeContainer>
        </>
    );
};

Hallway.propTypes = {
    netcoins: PropTypes.bool,
    location: PropTypes.string,
    onFocusChange: PropTypes.bool,
    setIsHallwayOpen: PropTypes.func.isRequired,
    isHallwayOpen: PropTypes.bool.isRequired,
    hallwayPrompt: PropTypes.string,
};

export default Hallway;
const IframeContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    position: fixed;
    left: 0;
    top: 18vh;
    z-index: 1000000;
    flex-direction: column;
    height: 350px;
    transform: ${({ isHallwayOpen }) => (isHallwayOpen ? "translateX(0)" : "translateX(-100%)")};
    transition: transform 0.3s ease-in-out;
    width: ${({ isHallwayOpen }) => (isHallwayOpen ? "220px" : "0")};

    @media (max-width: 768px) and (orientation: landscape) {
        padding: 2rem 0;
        bottom: 0;
    }

    @media (max-height: 470px) and (orientation: landscape) {
        height: 230px;
        top: 13vh;
        width: ${({ isHallwayOpen }) => (isHallwayOpen ? "140px" : "0")};
    }

    @media (max-width: 768px) and (orientation: portrait) {
        position: fixed;
        top: 0vh;
        left: 90px;
        transform-origin: top left;
        transform: rotate(-90deg) translateX(-100%);
        z-index: 100000;
        height: 200px;
    }

    @media (orientation: portrait) {
        position: fixed;
        bottom: 0vh;
        left: 30px;
        transform: none;
        z-index: 100000;
    }
`;

const RefocusButton = styled.button`
    display: ${({ isHallwayOpen }) => (isHallwayOpen ? "flex" : "none")};
    height: 40px;
    width: 100%;
    border: grey 0.8px solid;
    border-radius: 9px;
    background: black;
    color: white;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0px;
    font-family: "Segoe UI", sans-serif;
    margin-top: 0px;
    z-index: 1200000;
`;

const WidgetWrapper = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
`;

const ToggleHallwayIcon = styled.img`
    position: absolute;
    top: calc(20vh + 150px);
    left: ${({ isHallwayOpen }) => (isHallwayOpen ? "220px" : "0rem")};
    width: 50px;
    height: 50px;
    cursor: pointer;
    z-index: 101;
    background-color: rgba(0, 0, 0, 0.85);
    border-radius: 0px 4.152px 4.152px 0px;
    padding: 0.5rem;
    border-top: 1.261px solid rgba(255, 255, 255, 0.2);
    border-right: 1.261px solid rgba(255, 255, 255, 0.2);
    border-bottom: 1.261px solid rgba(255, 255, 255, 0.2);
    background: rgba(0, 0, 0, 0.55);
    z-index: 1000000;

    @media (max-width: 768px) and (orientation: landscape) {
        bottom: 0;
    }
    @media (orientation: portrait) {
        left: ${({ isHallwayOpen }) => (isHallwayOpen ? "250px" : "0")};
    }

    @media (max-height: 470px) and (orientation: landscape) {
        top: calc(20vh + 70px);
        width: 40px;
        height: 40px;
        left: ${({ isHallwayOpen }) => (isHallwayOpen ? "140px" : "0")};
    }

    @media (max-width: 768px) and (orientation: portrait) {
        left: ${({ isHallwayOpen }) => (isHallwayOpen ? "250px" : "0")};
        top: ${({ isHallwayOpen }) => (isHallwayOpen ? "10vw" : "0")};
    }
`;

// Volume button and slider styled-components
const VolumeContainer = styled.div`
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    height: auto;

    @media (max-width: 768px) {
        top: 0.2rem;
        left: 0.2rem;
    }
`;

const VolumeSquare = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    border: ${({ volumeOpen }) => (volumeOpen ? "2px" : "1px")} solid #beaeff;
    background: rgba(0, 0, 0, 0.85);
    z-index: 1002;
    cursor: pointer;
    height: 40px;
    width: 40px;
    margin-bottom: 0.2rem;
    opacity: ${({ volume }) => (volume > 5 ? 1 : 0.6)};

    /* Child img styling */
    img {
        width: 20px;
        height: 20px;
    }

    @media (max-width: 768px) {
        height: 32px;
        width: 32px;
        margin-bottom: 0;
        img {
            width: 16px;
            height: 16px;
        }
    }
`;

const SliderWrapper = styled.div`
    display: flex;
    align-items: center;
    height: 120px;
    max-height: 90%;

    @media (max-height: 350px) {
        height: 60px;
    }
    @media (max-width: 768px) {
        height: 80px;
    }
`;
